import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Grid, theme, Tooltip, Typography } from 'antd';
import { CardContent, MetricCardEcho } from './common';
import { formatNumber } from '@/utils/numberFormattingUtils';
import { useLocale } from '@acast-tech/decibel';
import { useEarningsQuery } from '@/services/revenueApi';
import useFetchQuery from '@/hooks/useFetchQuery';
import type { EarningsApiResponse } from '@/types/api';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { useState } from 'react';

const TotalEstimatedImpressionsCardEcho = ({ className }: { className?: string }) => {
  const { queryResult: earnings } = useFetchQuery(useEarningsQuery);

  return (
    <MetricCardEcho
      className={className}
      testId="total-estimated-impressions-card"
      title="Total estimated impressions"
      body={<CardContent {...earnings} renderData={(data: EarningsApiResponse) => <Data data={data} />} />}
    />
  );
};

const Data = ({ data }: { data: EarningsApiResponse }) => {
  const { locale } = useLocale();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMdUp = !!screens.md;
  const [cooldown, setCooldown] = useState(false);

  const handleMouseOver = () => {
    if (!cooldown) {
      mixpanelTrack(MIXPANEL_EVENT.earnings.totalEstimatedImpressions.tooltipHover, {
        widget: RevenueDashboardWidget.TotalEstimatedImpressions,
        page: 'earnings',
      });

      // We don't want to spam events so a 5 second cooldown here to prevent multiple events from being triggered
      setCooldown(true);
      setTimeout(() => {
        setCooldown(false);
      }, 5000);
    }
  };

  return (
    <Flex vertical gap={token.marginXXS}>
      <Typography.Title level={3} ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
        {formatNumber(locale, data.total_network_estimated_impressions ?? 0)}
      </Typography.Title>
      <Tooltip
        placement="topRight"
        title="This total includes only impressions delivered for campaigns paid on an impression basis. It does not include campaigns with other payment models."
      >
        <Flex align="center" gap="small" onMouseOver={handleMouseOver}>
          <Typography.Text
            ellipsis
            style={{
              maxWidth: isMdUp ? undefined : token.size * 8,
              fontSize: token.fontSizeSM,
              fontStyle: 'italic',
              color: token.colorTextDescription,
            }}
          >
            for impression-based campaigns only
          </Typography.Text>
          <InfoCircleOutlined
            style={{
              fontSize: token.fontSize,
              color: token.colorTextDescription,
            }}
            aria-hidden
          />
        </Flex>
      </Tooltip>
    </Flex>
  );
};

export default TotalEstimatedImpressionsCardEcho;
